import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Clinic {
  id: number;
  name: string;
  address: string;
  phone_number: string;
  email: string;
}

interface Species {
  id: number;
  name: string;
  breed_count?: number;
  is_active: number;
}

interface Breed {
  id: number;
  species_id: number;
  species_name: string;
  name: string;
  is_active: number;
}

const SettingsPage = () => {
  const [tab, setTab] = useState(0);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [species, setSpecies] = useState<Species[]>([]);
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clinicDialogOpen, setClinicDialogOpen] = useState(false);
  const [speciesDialogOpen, setSpeciesDialogOpen] = useState(false);
  const [breedDialogOpen, setBreedDialogOpen] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState<Species | null>(null);
  const [editingBreed, setEditingBreed] = useState<Breed | null>(null);
  const [clinicForm, setClinicForm] = useState({ name: '', address: '', phone_number: '', email: '' });
  const [speciesForm, setSpeciesForm] = useState({ name: '' });
  const [breedForm, setBreedForm] = useState({ species_id: '', name: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clinicRes, speciesRes, breedsRes] = await Promise.all([
        api.get<Clinic>(API_ENDPOINTS.ADMIN.LOCATIONS),
        api.get<{ data: Species[] }>(API_ENDPOINTS.PATIENTS.SPECIES),
        api.get<{ data: Breed[] }>(API_ENDPOINTS.PATIENTS.BREEDS),
      ]);
      setClinic(clinicRes);
      setSpecies(speciesRes.data ?? []);
      setBreeds(breedsRes.data ?? []);
    } catch (err) {
      setError('Failed to load settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClinic = async () => {
    try {
      await api.put(API_ENDPOINTS.ADMIN.LOCATIONS, clinicForm);
      setClinicDialogOpen(false);
      fetchData();
    } catch (err) {
      setError('Failed to update clinic info');
      console.error(err);
    }
  };

  const handleCreateSpecies = async () => {
    try {
      await api.post(API_ENDPOINTS.PATIENTS.SPECIES, speciesForm);
      setSpeciesDialogOpen(false);
      setSpeciesForm({ name: '' });
      fetchData();
    } catch (err) {
      setError('Failed to create species');
      console.error(err);
    }
  };

  const handleUpdateSpecies = async () => {
    if (!editingSpecies) return;
    try {
      await api.put(`${API_ENDPOINTS.PATIENTS.SPECIES}/${editingSpecies.id}`, speciesForm);
      setSpeciesDialogOpen(false);
      setEditingSpecies(null);
      setSpeciesForm({ name: '' });
      fetchData();
    } catch (err) {
      setError('Failed to update species');
      console.error(err);
    }
  };

  const handleDeleteSpecies = async (id: number) => {
    if (!confirm('Are you sure you want to delete this species?')) return;
    try {
      await api.delete(`${API_ENDPOINTS.PATIENTS.SPECIES}/${id}`);
      fetchData();
    } catch (err) {
      setError('Failed to delete species');
      console.error(err);
    }
  };

  const handleCreateBreed = async () => {
    try {
      await api.post(API_ENDPOINTS.PATIENTS.BREEDS, {
        ...breedForm,
        species_id: parseInt(breedForm.species_id),
      });
      setBreedDialogOpen(false);
      setBreedForm({ species_id: '', name: '' });
      fetchData();
    } catch (err) {
      setError('Failed to create breed');
      console.error(err);
    }
  };

  const handleUpdateBreed = async () => {
    if (!editingBreed) return;
    try {
      await api.put(`${API_ENDPOINTS.PATIENTS.BREEDS}/${editingBreed.id}`, {
        ...breedForm,
        species_id: parseInt(breedForm.species_id),
      });
      setBreedDialogOpen(false);
      setEditingBreed(null);
      setBreedForm({ species_id: '', name: '' });
      fetchData();
    } catch (err) {
      setError('Failed to update breed');
      console.error(err);
    }
  };

  const handleDeleteBreed = async (id: number) => {
    if (!confirm('Are you sure you want to delete this breed?')) return;
    try {
      await api.delete(`${API_ENDPOINTS.PATIENTS.BREEDS}/${id}`);
      fetchData();
    } catch (err) {
      setError('Failed to delete breed');
      console.error(err);
    }
  };

  const openEditSpecies = (s: Species) => {
    setEditingSpecies(s);
    setSpeciesForm({ name: s.name });
    setSpeciesDialogOpen(true);
  };

  const openEditBreed = (b: Breed) => {
    setEditingBreed(b);
    setBreedForm({ species_id: b.species_id.toString(), name: b.name });
    setBreedDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Settings</Typography>
      
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Clinic Info" />
        <Tab label="Species" />
        <Tab label="Breeds" />
      </Tabs>

      {tab === 0 && clinic && (
        <>
          <Paper sx={{ p: 3, maxWidth: 600 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Clinic Information</Typography>
              <Button onClick={() => { setClinicForm({ name: clinic.name, address: clinic.address, phone_number: clinic.phone_number, email: clinic.email }); setClinicDialogOpen(true); }}>
                Edit
              </Button>
            </Box>
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">Name</Typography>
              <Typography>{clinic.name}</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">Address</Typography>
              <Typography>{clinic.address || 'Not set'}</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
              <Typography>{clinic.phone_number || 'Not set'}</Typography>
            </Box>
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">Email</Typography>
              <Typography>{clinic.email || 'Not set'}</Typography>
            </Box>
          </Paper>
        </>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingSpecies(null); setSpeciesForm({ name: '' }); setSpeciesDialogOpen(true); }}>
              Add Species
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Breeds</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {species.map(s => (
                  <TableRow key={s.id}>
                    <TableCell>{s.name}</TableCell>
                    <TableCell>{s.breed_count || 0}</TableCell>
                    <TableCell>
                      <Chip label={s.is_active ? 'Active' : 'Inactive'} color={s.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openEditSpecies(s)}><EditIcon /></IconButton>
                      <IconButton size="small" onClick={() => handleDeleteSpecies(s.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {species.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">No species found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {tab === 2 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingBreed(null); setBreedForm({ species_id: '', name: '' }); setBreedDialogOpen(true); }}>
              Add Breed
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Species</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breeds.map(b => (
                  <TableRow key={b.id}>
                    <TableCell>{b.name}</TableCell>
                    <TableCell>{b.species_name}</TableCell>
                    <TableCell>
                      <Chip label={b.is_active ? 'Active' : 'Inactive'} color={b.is_active ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openEditBreed(b)}><EditIcon /></IconButton>
                      <IconButton size="small" onClick={() => handleDeleteBreed(b.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {breeds.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">No breeds found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={clinicDialogOpen} onClose={() => setClinicDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Clinic Information</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Name" value={clinicForm.name}
            onChange={e => setClinicForm({ ...clinicForm, name: e.target.value })} />
          <TextField fullWidth margin="normal" label="Address" value={clinicForm.address}
            onChange={e => setClinicForm({ ...clinicForm, address: e.target.value })} />
          <TextField fullWidth margin="normal" label="Phone" value={clinicForm.phone_number}
            onChange={e => setClinicForm({ ...clinicForm, phone_number: e.target.value })} />
          <TextField fullWidth margin="normal" label="Email" value={clinicForm.email}
            onChange={e => setClinicForm({ ...clinicForm, email: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClinicDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateClinic} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={speciesDialogOpen} onClose={() => setSpeciesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSpecies ? 'Edit Species' : 'Add Species'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Name" value={speciesForm.name}
            onChange={e => setSpeciesForm({ name: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpeciesDialogOpen(false)}>Cancel</Button>
          <Button onClick={editingSpecies ? handleUpdateSpecies : handleCreateSpecies} variant="contained">
            {editingSpecies ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={breedDialogOpen} onClose={() => setBreedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBreed ? 'Edit Breed' : 'Add Breed'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" select label="Species" value={breedForm.species_id}
            onChange={e => setBreedForm({ ...breedForm, species_id: e.target.value })}>
            {species.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </TextField>
          <TextField fullWidth margin="normal" label="Name" value={breedForm.name}
            onChange={e => setBreedForm({ ...breedForm, name: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBreedDialogOpen(false)}>Cancel</Button>
          <Button onClick={editingBreed ? handleUpdateBreed : handleCreateBreed} variant="contained">
            {editingBreed ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
